import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Typography,
  TextField,
  Grid,
  Button,
  Tabs,
  Tab,
  Chip,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import api from "../services/api";

const STATUSES = ["Scheduled", "Completed", "Cancelled"];

const STATUS_COLOR = {
  Scheduled: "warning",
  Completed: "success",
  Cancelled: "default",
};

const emptyForm = {
  SaleOrder_ID: null,
  SO: "",
  Customer_Name: "",
  Item_Description: "",
  Qty: 1,
  PickupDate: "",
  PickupTime: "",
  DriverName: "",
  Notes: "",
};

export default function PickupScheduled() {
  const [tab, setTab] = useState(0);

  // ---- schedule form ----
  const [soRows, setSoRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [selectedSO, setSelectedSO] = useState(null); // for order-qty / remaining display

  // ---- active list ----
  const [pickups, setPickups] = useState([]);
  const [statusFilter, setStatusFilter] = useState("Scheduled");

  // ---- edit dialog ----
  const [editRow, setEditRow] = useState(null); // the pickup being edited (null = closed)

  const loadSO = async () => {
    try {
      const res = await api.get("/pickup/lookup/so");
      setSoRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      setSoRows([]);
    }
  };

  const loadPickups = useCallback(async () => {
    try {
      const res = await api.get("/pickup", {
        params: statusFilter ? { status: statusFilter } : {},
      });
      setPickups(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      setPickups([]);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadSO();
  }, []);

  useEffect(() => {
    loadPickups();
  }, [loadPickups]);

  // ---- schedule a pickup ----
  const savePickup = async () => {
    if (!form.SaleOrder_ID && !form.SO) return alert("Select a Sale Order");
    if (!form.PickupDate) return alert("Pickup date required");

    try {
      await api.post("/pickup", { ...form, Status: "Scheduled" });
      alert("Pickup scheduled");
      setForm(emptyForm);
      setSelectedSO(null);
      await loadSO(); // refresh scheduled/remaining counts
      await loadPickups();
      setTab(1); // jump to the active list so they see it
    } catch (err) {
      console.error(err);
      alert("Failed to schedule pickup");
    }
  };

  // ---- mark complete / cancel ----
  const setStatus = async (p, status) => {
    try {
      await api.put(`/pickup/${p.Pickup_ID}`, {
        Qty: p.Qty || 1,
        PickupDate: p.PickupDate ? String(p.PickupDate).split("T")[0] : null,
        PickupTime: p.PickupTime || null,
        DriverName: p.DriverName || null,
        Notes: p.Notes || null,
        Status: status,
      });
      await loadPickups();
    } catch (err) {
      console.error(err);
      alert("Failed to update pickup");
    }
  };

  // ---- edit an existing pickup (Loads / date / time / driver / notes / status) ----
  const openEdit = (p) => {
    setEditRow({
      Pickup_ID: p.Pickup_ID,
      SO: p.SO,
      Customer_Name: p.Customer_Name,
      Item_Description: p.Item_Description,
      Qty: p.Qty ?? 1,
      PickupDate: p.PickupDate ? String(p.PickupDate).split("T")[0] : "",
      PickupTime: p.PickupTime || "",
      DriverName: p.DriverName || "",
      Notes: p.Notes || "",
      Status: p.Status || "Scheduled",
    });
  };

  const saveEdit = async () => {
    if (!editRow) return;
    if (!editRow.PickupDate) return alert("Pickup date required");
    try {
      await api.put(`/pickup/${editRow.Pickup_ID}`, {
        Qty: Number(editRow.Qty) > 0 ? Number(editRow.Qty) : 1,
        PickupDate: editRow.PickupDate,
        PickupTime: editRow.PickupTime || null,
        DriverName: editRow.DriverName || null,
        Notes: editRow.Notes || null,
        Status: editRow.Status || "Scheduled",
      });
      setEditRow(null);
      await loadPickups();
      await loadSO(); // Loads may have changed -> refresh scheduled/remaining counts
    } catch (err) {
      console.error(err);
      alert("Failed to update pickup");
    }
  };

  const deletePickup = async (pickupId) => {
    if (!window.confirm("Remove this pickup?")) return;
    try {
      await api.delete(`/pickup/${pickupId}`);
      await loadPickups();
    } catch (err) {
      console.error(err);
    }
  };

  const fmtDate = (d) => (d ? String(d).split("T")[0] : "");

  return (
    <Box p={2}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
        Pickup Scheduled
      </Typography>

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Schedule a Pickup" />
        <Tab label="Active Pickups" />
      </Tabs>

      {/* ============ SCHEDULE TAB ============ */}
      {tab === 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            New Pickup
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                options={soRows}
                getOptionLabel={(o) =>
                  o?.SO ? `SO# ${o.SO}${o.Name ? " — " + o.Name : ""}` : ""
                }
                value={soRows.find((s) => s.Master_ID === form.SaleOrder_ID) || null}
                onChange={(e, v) => {
                  setSelectedSO(v || null);
                  const remaining = v ? Math.max(1, (v.Qty || 0) - (v.ScheduledLoads || 0)) : 1;
                  setForm({
                    ...form,
                    SaleOrder_ID: v?.Master_ID || null,
                    SO: v?.SO || "",
                    Customer_Name: v?.Name || "",
                    Item_Description: v?.Item_Description || "",
                    Qty: remaining,
                  });
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Sale Order (SO#)" fullWidth />
                )}
                fullWidth
              />
              {selectedSO && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                  Order Qty: <b>{selectedSO.Qty || 0}</b> · Already scheduled:{" "}
                  <b>{selectedSO.ScheduledLoads || 0}</b> · Remaining:{" "}
                  <b>{Math.max(0, (selectedSO.Qty || 0) - (selectedSO.ScheduledLoads || 0))}</b>
                </Typography>
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Customer"
                fullWidth
                value={form.Customer_Name}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Product"
                fullWidth
                value={form.Item_Description}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <TextField
                type="number"
                label="Loads"
                inputProps={{ min: 1 }}
                fullWidth
                value={form.Qty}
                onChange={(e) => setForm({ ...form, Qty: e.target.value })}
                helperText="loads on this date"
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <TextField
                type="date"
                label="Pickup Date"
                InputLabelProps={{ shrink: true }}
                fullWidth
                value={form.PickupDate}
                onChange={(e) => setForm({ ...form, PickupDate: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <TextField
                label="Pickup Time"
                placeholder="e.g. 9:00 AM"
                fullWidth
                value={form.PickupTime}
                onChange={(e) => setForm({ ...form, PickupTime: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Driver / Carrier"
                fullWidth
                value={form.DriverName}
                onChange={(e) => setForm({ ...form, DriverName: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Notes"
                fullWidth
                value={form.Notes}
                onChange={(e) => setForm({ ...form, Notes: e.target.value })}
              />
            </Grid>
          </Grid>
          <Box mt={2} sx={{ display: "flex", gap: 2 }}>
            <Button variant="contained" onClick={savePickup}>
              Schedule Pickup
            </Button>
            <Button variant="outlined" onClick={() => { setForm(emptyForm); setSelectedSO(null); }}>
              Clear
            </Button>
          </Box>
        </Paper>
      )}

      {/* ============ ACTIVE PICKUPS TAB ============ */}
      {tab === 1 && (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <Typography variant="h6">Pickups by Date</Typography>
            <TextField
              select
              size="small"
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">All</MenuItem>
              {STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Pickup Date</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>SO#</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell align="right">Loads</TableCell>
                  <TableCell>Driver / Carrier</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pickups.map((p) => (
                  <TableRow key={p.Pickup_ID} hover>
                    <TableCell>{fmtDate(p.PickupDate)}</TableCell>
                    <TableCell>{p.PickupTime || ""}</TableCell>
                    <TableCell>{p.SO ? `SO# ${p.SO}` : ""}</TableCell>
                    <TableCell>{p.Customer_Name || ""}</TableCell>
                    <TableCell>{p.Item_Description || ""}</TableCell>
                    <TableCell align="right">{p.Qty ?? ""}</TableCell>
                    <TableCell>{p.DriverName || ""}</TableCell>
                    <TableCell>{p.Notes || ""}</TableCell>
                    <TableCell>
                      <Chip
                        label={p.Status}
                        size="small"
                        color={STATUS_COLOR[p.Status] || "default"}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      <Button
                        size="small"
                        color="primary"
                        onClick={() => openEdit(p)}
                      >
                        Edit
                      </Button>
                      {p.Status !== "Completed" && (
                        <Button
                          size="small"
                          color="success"
                          onClick={() => setStatus(p, "Completed")}
                        >
                          Done
                        </Button>
                      )}
                      {p.Status !== "Cancelled" && (
                        <Button
                          size="small"
                          color="warning"
                          onClick={() => setStatus(p, "Cancelled")}
                        >
                          Cancel
                        </Button>
                      )}
                      <Button
                        size="small"
                        color="error"
                        onClick={() => deletePickup(p.Pickup_ID)}
                      >
                        Remove
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {pickups.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ color: "text.secondary", py: 3 }}>
                      No pickups{statusFilter ? ` with status "${statusFilter}"` : ""}.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ============ EDIT PICKUP DIALOG ============ */}
      <Dialog open={!!editRow} onClose={() => setEditRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit Pickup{editRow?.SO ? ` — SO# ${editRow.SO}` : ""}
        </DialogTitle>
        <DialogContent dividers>
          {editRow && (
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Customer"
                  fullWidth
                  value={editRow.Customer_Name || ""}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Product"
                  fullWidth
                  value={editRow.Item_Description || ""}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <TextField
                  type="number"
                  label="Loads"
                  inputProps={{ min: 1 }}
                  fullWidth
                  value={editRow.Qty}
                  onChange={(e) => setEditRow({ ...editRow, Qty: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 4 }}>
                <TextField
                  type="date"
                  label="Pickup Date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  value={editRow.PickupDate}
                  onChange={(e) => setEditRow({ ...editRow, PickupDate: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 5 }}>
                <TextField
                  label="Pickup Time"
                  placeholder="e.g. 9:00 AM"
                  fullWidth
                  value={editRow.PickupTime}
                  onChange={(e) => setEditRow({ ...editRow, PickupTime: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Driver / Carrier"
                  fullWidth
                  value={editRow.DriverName}
                  onChange={(e) => setEditRow({ ...editRow, DriverName: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  select
                  label="Status"
                  fullWidth
                  value={editRow.Status}
                  onChange={(e) => setEditRow({ ...editRow, Status: e.target.value })}
                >
                  {STATUSES.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Notes"
                  fullWidth
                  value={editRow.Notes}
                  onChange={(e) => setEditRow({ ...editRow, Notes: e.target.value })}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRow(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveEdit}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
